import { DomainError } from "./errors";
import { cleanChannels, isIsoDate } from "./reminders";
import { DEFAULT_COUNTRY, isValidPostalCode } from "./address";

/** Customer as the program handles it (database columns are snake_case). */
export interface CustomerInput {
  legacyId?: string | null;
  name: string;
  isCompany?: boolean;
  taxNumber?: string | null;
  /** Country of the address (ISO alpha-2); null means Hungary (owner, 2026-11-04: invoices need it). */
  country?: string | null;
  postalCode?: string | null;
  city?: string | null;
  addressLine?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  contactPerson?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  /** Reminder channels the customer consented to ("kér emlékeztetőt"); empty = no consent. */
  reminderChannels?: ("sms" | "email")[];
}

export interface VehicleInput {
  legacyId?: string | null;
  customerId?: string | null;
  plateNumber: string;
  /** Country of the plate (ISO alpha-2); null means the workshop's own country. */
  plateCountry?: string | null;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  typeDescription?: string | null;
  engineCode?: string | null;
  powerKw?: number | null;
  modelYear?: number | null;
  fuelType?: string | null;
  tyreSize?: string | null;
  odometerKm?: number | null;
  notes?: string | null;
  /** Technical inspection valid until (YYYY-MM-DD). */
  inspectionValidUntil?: string | null;
  /** Next service: date (YYYY-MM-DD) and/or odometer reading. */
  nextServiceDate?: string | null;
  nextServiceKm?: number | null;
  /** Whether each reminder is sent (ticked by default; a service reminder needs a service date). */
  remindInspection?: boolean;
  remindService?: boolean;
}

/** Empty strings become null, other strings are trimmed. */
export function clean(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const v = value.trim();
  return v === "" ? null : v;
}

/** Display form of a plate: upper case, single spaces. Keeps the user's dash or space. */
export function normalizePlate(plate: string): string {
  return plate.trim().toUpperCase().replace(/\s+/g, " ");
}

/** Search key of a plate, identical to the database column vehicles.plate_search. */
export function plateSearchKey(plate: string): string {
  return plate.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** A VIN has 17 characters, letters and digits but never I, O or Q. */
export function isValidVin(vin: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim().toUpperCase());
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** An e-mail address that can be right: name, @, domain with a dot, no spaces. */
export function isPlausibleEmail(email: string): boolean {
  return EMAIL.test(email.trim());
}

/**
 * One phone number without spaces and separators: "06 30 123-4567" becomes
 * "06301234567", "+36 (30) 123 4567" becomes "+36301234567" (both +36 and 06
 * stay as typed). Hungarian numbers as stored by many old programs lose their
 * leading zero (6204314021): the domestic "06" prefix is restored. Text that is
 * not a single number (several numbers, a note) is kept as it is.
 * The same rules run in the database once for the existing data
 * (supabase/migrations/20261109000000_phone_format.sql).
 */
export function normalizePhone(phone: string | null | undefined): string | null {
  let p = clean(phone);
  if (!p) return null;
  if (/^\d+\.0$/.test(p)) p = p.slice(0, -2);
  if (/^\+?[\d ()./-]+$/.test(p)) p = p.replace(/[ ()./-]/g, "");
  if (/^\d+$/.test(p)) {
    if (p.length === 10 && p.startsWith("6")) return `0${p}`;
    if (p.length === 9 && /^(20|30|31|50|70)/.test(p)) return `06${p}`;
    if (p.length === 11 && p.startsWith("36")) return `+${p}`;
  }
  return p;
}

// legal forms; also sole traders (e.v., egyéni vállalkozó), like isCompanyName in the company lookup
const COMPANY = /\b(kft|bt|zrt|nyrt|kkt|kht|rt|nonprofit|egyesület|alapítvány|gmbh|s\.?r\.?o|d\.?o\.?o|ltd)\b\.?|(^|\s)e\.\s?v\.?($|\s)|\bev\.?$|egyéni vállalkozó/i;

export function looksLikeCompany(name: string): boolean {
  return COMPANY.test(name);
}

export function validateCustomer(c: CustomerInput): CustomerInput {
  const name = clean(c.name);
  if (!name) throw new DomainError("CUSTOMER_NAME_REQUIRED");
  const email = clean(c.email);
  if (email && !EMAIL.test(email)) throw new DomainError("INVALID_EMAIL", email);
  return {
    ...c,
    name,
    email,
    phone: normalizePhone(c.phone),
    mobile: normalizePhone(c.mobile),
    contactPhone: normalizePhone(c.contactPhone),
    isCompany: c.isCompany ?? looksLikeCompany(name),
    ...(c.country !== undefined ? { country: /^[A-Z]{2}$/.test((c.country ?? "").trim().toUpperCase()) && c.country!.trim().toUpperCase() !== DEFAULT_COUNTRY ? c.country!.trim().toUpperCase() : null } : {}),
    ...(c.reminderChannels !== undefined ? { reminderChannels: cleanChannels(c.reminderChannels) } : {}),
  };
}

/**
 * Validates a vehicle. An invalid VIN is rejected only for new entries
 * (strict); imported data from the old program may contain placeholders and is kept.
 */
export function validateVehicle(v: VehicleInput, options: { strictVin?: boolean } = {}): VehicleInput {
  const plate = clean(v.plateNumber);
  if (!plate) throw new DomainError("PLATE_REQUIRED");
  const vin = clean(v.vin)?.toUpperCase() ?? null;
  if (vin && options.strictVin !== false && !isValidVin(vin)) throw new DomainError("INVALID_VIN", vin);
  const out: VehicleInput = { ...v, plateNumber: normalizePlate(plate), vin, engineCode: clean(v.engineCode), make: clean(v.make) };
  // reminder dates: a calendar day or nothing (only touched when the form has them)
  for (const k of ["inspectionValidUntil", "nextServiceDate"] as const) {
    if (k in v) out[k] = isIsoDate(clean(v[k] ?? null)) ? clean(v[k] ?? null) : null;
  }
  for (const k of ["remindInspection", "remindService"] as const) {
    if (k in v) out[k] = v[k] !== false;
  }
  if ("nextServiceKm" in v) out.nextServiceKm = typeof v.nextServiceKm === "number" && Number.isInteger(v.nextServiceKm) && v.nextServiceKm >= 0 ? v.nextServiceKm : null;
  return out;
}

/** A field whose value is filled but cannot be right; `messageKey` explains how to fix it. */
export interface FieldProblem {
  field: string;
  messageKey: string;
  params?: Record<string, string | number>;
}

/** Oldest model year the database accepts (vehicles.model_year check). */
export const MIN_MODEL_YEAR = 1900;

/** A phone number has at least 6 digits; a note next to it ("06 30 123 4567 (fia)") is fine. */
export function isPlausiblePhone(phone: string): boolean {
  return (phone.match(/\d/g) ?? []).length >= 6;
}

/**
 * A tax number: Hungarian 8, 10 or 11 digits (12345678-2-13), or a foreign one
 * starting with its country code (ATU12345678, DE123456789) with at least 7 digits.
 */
export function isPlausibleTaxNumber(taxNumber: string): boolean {
  const t = taxNumber.trim().toUpperCase().replace(/[\s.\-/]/g, "");
  if (/^\d+$/.test(t)) return [8, 10, 11].includes(t.length);
  return /^[A-Z]{2}[A-Z0-9+*]{2,13}$/.test(t) && (t.match(/\d/g) ?? []).length >= 7;
}

const changed = <T>(input: T, original: T | undefined, key: keyof T) => !original || input[key] !== original[key];

/**
 * Filled customer fields that cannot be right. Only fields the user typed are
 * checked (new customer, or changed against `original`), so data kept from
 * the old program never blocks an everyday save.
 */
export function customerFieldProblems(c: CustomerInput, original?: CustomerInput): FieldProblem[] {
  const out: FieldProblem[] = [];
  for (const field of ["mobile", "phone", "contactPhone"] as const) {
    const v = clean(c[field]);
    if (v && changed(c, original, field) && !isPlausiblePhone(v)) out.push({ field, messageKey: "invalid.phone" });
  }
  const tax = clean(c.taxNumber);
  if (tax && changed(c, original, "taxNumber") && !isPlausibleTaxNumber(tax)) out.push({ field: "taxNumber", messageKey: "invalid.taxNumber" });
  const email = clean(c.email);
  if (email && changed(c, original, "email") && !EMAIL.test(email)) out.push({ field: "email", messageKey: "invalid.email" });
  // the postal code in the country's format (Hungary: 4 digits), checked like the workshop's own addresses
  const postal = clean(c.postalCode);
  if (postal && (changed(c, original, "postalCode") || changed(c, original, "country")) && !isValidPostalCode(postal, c.country ?? DEFAULT_COUNTRY)) {
    out.push({ field: "postalCode", messageKey: "invalid.postalCode" });
  }
  return out;
}

/** Filled vehicle fields that cannot be right (VIN, model year, odometer, power). */
export function vehicleFieldProblems(v: VehicleInput, original?: VehicleInput, today: Date = new Date()): FieldProblem[] {
  const out: FieldProblem[] = [];
  // a stored VIN (e.g. a placeholder from the old program) is checked only when it changes (#119)
  const vin = clean(v.vin);
  if (vin && (!original || vin.toUpperCase() !== (clean(original.vin) ?? "").toUpperCase()) && !isValidVin(vin)) {
    out.push({ field: "vin", messageKey: "invalid.vin" });
  }
  const maxYear = today.getFullYear() + 1;
  const y = v.modelYear;
  if (y !== null && y !== undefined && changed(v, original, "modelYear") && (!Number.isInteger(y) || y < MIN_MODEL_YEAR || y > maxYear)) {
    out.push({ field: "modelYear", messageKey: "invalid.modelYear", params: { min: MIN_MODEL_YEAR, max: maxYear } });
  }
  const km = v.odometerKm;
  if (km !== null && km !== undefined && changed(v, original, "odometerKm") && km < 0) out.push({ field: "odometerKm", messageKey: "invalid.odometerKm" });
  const kw = v.powerKw;
  if (kw !== null && kw !== undefined && changed(v, original, "powerKw") && kw <= 0) out.push({ field: "powerKw", messageKey: "invalid.powerKw" });
  return out;
}
